<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import paymentService from '../services/payment.service'
import bankAccountService from '../services/bankAccount.service'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || null)
const paymentUuid = computed(() => route.params.uuid as string)

const loading = ref(false)
const payment = ref<any>(null)
const bankAccounts = ref<any[]>([])

const formData = ref({
  name: '',
  amount: '',
  due_date: '',
  bank_account_uuid: '',
  description: ''
})

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canUpdatePayment = computed(() => {
  return hasPermission('PAYMENT_UPDATE')
})

// Fetch bank accounts
const fetchBankAccounts = async () => {
  if (!clubUuid.value) return

  try {
    const response = await bankAccountService.getBankAccounts(clubUuid.value)
    bankAccounts.value = response.bank_accounts || []
  } catch (error: any) {
    console.error('Error fetching bank accounts:', error)
  }
}

// Fetch payment details
const fetchPayment = async () => {
  if (!clubUuid.value || !paymentUuid.value) return

  loading.value = true
  try {
    payment.value = await paymentService.getPayment(clubUuid.value, paymentUuid.value)

    // Check if payment is paid - redirect if yes
    if (payment.value.status === 'paid') {
      toastStore.error(t('editPayment.cannotEditPaidPayment'))
      router.push('/payments')
      return
    }

    // Fill form with payment data
    formData.value = {
      name: payment.value.name || '',
      amount: payment.value.amount || '',
      due_date: payment.value.due_date ? payment.value.due_date.split('T')[0] : '',
      bank_account_uuid: payment.value.bank_account_uuid || '',
      description: payment.value.description || ''
    }
  } catch (error: any) {
    console.error('Error fetching payment:', error)
    toastStore.error(t('editPayment.errorFetchingPayment'))
    router.push('/payments')
  } finally {
    loading.value = false
  }
}

// Update payment
const updatePayment = async () => {
  if (!clubUuid.value || !paymentUuid.value) return

  loading.value = true
  try {
    await paymentService.updatePayment(clubUuid.value, paymentUuid.value, formData.value)
    toastStore.success(t('editPayment.paymentUpdatedSuccessfully'))
    router.push('/payments')
  } catch (error: any) {
    console.error('Error updating payment:', error)
    const errorMessage = error.response?.data?.message || t('editPayment.errorUpdatingPayment')
    toastStore.error(errorMessage)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  if (!canUpdatePayment.value) {
    toastStore.error(t('editPayment.noPermission'))
    router.push('/payments')
    return
  }

  await fetchBankAccounts()
  await fetchPayment()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/payments" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('editPayment.backToList') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('editPayment.title') }}</h1>
      </div>

      <!-- Loading -->
      <div v-if="loading && !payment" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('editPayment.loading') }}</p>
      </div>

      <!-- Form -->
      <div v-if="payment" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="updatePayment">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Player Name (not editable, clickable) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('editPayment.playerName') }}
              </label>
              <div class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-50">
                <router-link
                  v-if="payment.player"
                  :to="`/players/${payment.player.uuid}`"
                  class="text-blue-600 hover:underline"
                >
                  {{ payment.player.first_name }} {{ payment.player.last_name }}
                </router-link>
              </div>
            </div>

            <!-- Created At (not editable) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('editPayment.createdAt') }}
              </label>
              <div class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-50 text-gray-700">
                {{ payment.created_at ? new Date(payment.created_at).toLocaleString('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '') : '' }}
              </div>
            </div>

            <!-- Payment Name -->
            <div>
              <label for="paymentName" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('editPayment.paymentName') }} <span class="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="paymentName"
                v-model="formData.name"
                required
                class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
              >
            </div>

            <!-- Due Date -->
            <div>
              <label for="dueDate" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('editPayment.dueDate') }} <span class="text-red-600">*</span>
              </label>
              <input
                type="date"
                id="dueDate"
                v-model="formData.due_date"
                required
                class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
              >
            </div>

            <!-- Bank Account -->
            <div>
              <label for="bankAccount" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('editPayment.bankAccount') }}
              </label>
              <select
                id="bankAccount"
                v-model="formData.bank_account_uuid"
                class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
              >
                <option value="">{{ t('editPayment.selectBankAccount') }}</option>
                <option v-for="account in bankAccounts" :key="account.uuid" :value="account.uuid">
                  {{ account.account_name }} - {{ account.account_number }}
                </option>
              </select>
            </div>

            <!-- Amount -->
            <div>
              <label for="amount" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('editPayment.amount') }} <span class="text-red-600">*</span>
              </label>
              <input
                type="number"
                id="amount"
                v-model="formData.amount"
                required
                min="0"
                step="0.01"
                class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
              >
            </div>

            <!-- Description -->
            <div class="md:col-span-2">
              <label for="description" class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('editPayment.description') }}
              </label>
              <textarea
                id="description"
                v-model="formData.description"
                rows="4"
                class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
              ></textarea>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end mt-6">
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50"
            >
              {{ loading ? t('editPayment.updating') : t('editPayment.updateButton') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
